import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fullName, useFlow } from '../state/flow';
import { useFitText } from '../lib/useFitText';
import type { FormData } from '../state/flow';
import './IDCard.scss';

export interface IDCardOverride {
  locale: string;
  form: FormData;
  photoDataUrl: string | null;
}

interface IDCardProps {
  /** When provided, renders against this data instead of the global flow + active locale. */
  override?: IDCardOverride;
}

// Card-local Y of the photo's bottom edge: $lockup-y + $photo-y + $photo-h
// (40 + 225 + 322). The bottom block centers in the region below this line.
const PHOTO_BOTTOM_Y = 587;
// Minimum gap above the bottom block — used when the right column extends
// past the photo (long names) so the block still clears the column with air.
const BOTTOM_GAP = 30;
// Card-local Y of the red student bar's top edge ($lockup-y + $footer-top).
const STUDENT_BAR_TOP = 922;
// Reserved breathing room above the red so content never crowds the bar.
const STUDENT_BAR_SAFETY = 16;

const IDCard = forwardRef<HTMLDivElement, IDCardProps>(({ override }, ref) => {
  const { t: tHook, i18n } = useTranslation();
  const flow = useFlow();

  const locale = override?.locale ?? i18n.resolvedLanguage ?? 'en';
  const form = override?.form ?? flow.form;
  const photoDataUrl = override?.photoDataUrl ?? flow.photoDataUrl;
  const t = override ? i18n.getFixedT(override.locale) : tHook;

  const topFieldsRef = useRef<HTMLDivElement | null>(null);
  const bottomFieldsRef = useRef<HTMLDivElement | null>(null);
  const [bottomTop, setBottomTop] = useState(700);
  // When NAME + MAJOR together overflow into 5+ lines, push MINOR down to the
  // bottom (non-indented) row so the indented column doesn't run away.
  const [minorOverflow, setMinorOverflow] = useState(false);

  useLayoutEffect(() => {
    const el = topFieldsRef.current;
    if (!el) return;
    // useFitText runs in child useLayoutEffects (which fire before this
    // parent one), so by the time we measure the column has already settled.
    let nextMinorOverflow = minorOverflow;
    const values = el.querySelectorAll<HTMLElement>('.id-card__value');
    if (values.length >= 2) {
      const lh = parseFloat(getComputedStyle(values[0]).lineHeight);
      if (Number.isFinite(lh) && lh > 0) {
        const nameLines = Math.round(values[0].offsetHeight / lh);
        const majorLines = Math.round(values[1].offsetHeight / lh);
        nextMinorOverflow = nameLines + majorLines >= 5;
      }
    }

    // Vertically center the bottom block in the region between the photo
    // bottom and the student bar — equal breathing room above and below.
    const naturalBottom = el.offsetTop + el.offsetHeight;
    const bottomEl = bottomFieldsRef.current;
    const blockHeight = bottomEl?.offsetHeight ?? 0;
    const slack = Math.max(0, STUDENT_BAR_TOP - PHOTO_BOTTOM_Y - blockHeight);
    let nextBottomTop = PHOTO_BOTTOM_Y + slack / 2;

    // If the right column extends past the photo and the centered block
    // would actually overlap its tail, push the block down to clear it.
    // (Doesn't fire unless there's a real conflict.)
    const rightColumnFloor = naturalBottom + BOTTOM_GAP;
    if (nextBottomTop < rightColumnFloor) {
      nextBottomTop = rightColumnFloor;
    }

    // Safety: if the block can't fit between the right column and the student
    // bar's reserved zone, pull it up — clamped so it never crosses the photo.
    const safetyBottom = STUDENT_BAR_TOP - STUDENT_BAR_SAFETY;
    const overshoot = nextBottomTop + blockHeight - safetyBottom;
    if (overshoot > 0) {
      nextBottomTop = Math.max(PHOTO_BOTTOM_Y, nextBottomTop - overshoot);
    }

    // Skip the setState pair when neither value changed — `minorOverflow` is in
    // the deps, so the effect re-fires after every flip; no-op renders here
    // would fire on every form keystroke.
    if (nextMinorOverflow === minorOverflow && nextBottomTop === bottomTop) return;
    setMinorOverflow(nextMinorOverflow);
    setBottomTop(nextBottomTop);
  }, [form, locale, minorOverflow, bottomTop]);

  return (
    <div ref={ref} className="id-card">
      <div className="id-card__photo">
        {photoDataUrl && <img src={photoDataUrl} alt="" />}
      </div>

      <div ref={topFieldsRef} className="id-card__top-fields">
        <Field label={t('card.name')} value={fullName(form)} />
        <Field label={t('card.label', { label: t('card.major') })} value={form.major} />
        {!minorOverflow && (
          <Field label={t('card.label', { label: t('card.minor') })} value={form.minor} />
        )}
      </div>

      <div
        ref={bottomFieldsRef}
        className="id-card__bottom-fields"
        style={{ top: `${bottomTop}px` }}
      >
        {minorOverflow && (
          <Field label={t('card.label', { label: t('card.minor') })} value={form.minor} />
        )}
        <ClubsField
          label={t('card.label', { label: t('form.club') })}
          items={form.clubs}
          // useFitText's budget is `maxLines * lineHeight`, which excludes the
          // 0.2em margin-top on every `<li>` — for N items that's an extra ~N*0.2
          // lineHeight of scrollHeight to absorb. 7 here covers up to ~6 wrapped
          // visual lines (e.g. 4 PT-club picks where two wrap to 2 lines each)
          // before shrinking, since the bottom block has the headroom. Falls
          // back to 4 when MINOR has been pushed down here, to leave it room.
          maxLines={minorOverflow ? 4 : 7}
        />
      </div>

      <div className="id-card__student-bar">{t('card.student')}</div>
    </div>
  );
});

IDCard.displayName = 'IDCard';

function Field({
  label,
  value,
  maxLines = 3,
}: {
  label: string;
  value: string;
  maxLines?: number;
}) {
  const valueRef = useFitText<HTMLDivElement>(value, { maxLines, minScale: 0.5 });
  return (
    <div className="id-card__row">
      <div className="id-card__label">{label}</div>
      <div ref={valueRef} className="id-card__value">
        {value || ' '}
      </div>
    </div>
  );
}

function ClubsField({
  label,
  items,
  maxLines,
}: {
  label: string;
  items: string[];
  maxLines: number;
}) {
  const valueRef = useFitText<HTMLUListElement>(items.join('\n'), { maxLines, minScale: 0.5 });
  return (
    <div className="id-card__row">
      <div className="id-card__label">{label}</div>
      <ul ref={valueRef} className="id-card__value id-card__clubs">
        {items.map((club) => (
          <li key={club}>{club}</li>
        ))}
      </ul>
    </div>
  );
}

export default IDCard;
